import { Close, NavigateBefore, NavigateNext } from '@mui/icons-material';
import { Box, CircularProgress, Dialog, DialogContent, IconButton, Typography } from '@mui/material';
import { useState } from 'react';
import ProductGrid from './ProductGrid';

const isImageUrl = (url) => /\.(?:jpe?g|png|gif|webp|avif|bmp|svg)(?:[?#]|$)|\/image\/upload\//i.test(url);

const getAssistantContent = (content = '') => {
  const images = [];
  let text = content.replace(/!?\[[^\]]*\]\((https?:\/\/[^)\s]+)\)/gi, (match, url) => {
    if (!isImageUrl(url)) return match;
    images.push(url);
    return '';
  });

  text = text.replace(/https?:\/\/[^\s<>)\]]+/gi, (rawUrl) => {
    const url = rawUrl.replace(/[.,!?;:]+$/, '');
    if (!isImageUrl(url)) return rawUrl;
    images.push(url);
    return '';
  });
  text = text.replace(/(?:["']?\b(?:available[_\s-]*)?(?:qty|quantity|stock)\b["']?|\bqty[_\s-]*available\b|\bquantity[_\s-]*available\b|\bcantidad(?:\s+disponible)?\b)\s*[:=]\s*["']?[\d.,]+["']?/gi, '');
  text = text.replace(/[ \t]{2,}/g, ' ').replace(/\s+([,.;])/g, '$1').trim();

  return { text, images: [...new Set(images)] };
};

export const UserMessage = ({ content }) => (
  <Box sx={{ alignSelf: 'flex-end', maxWidth: '82%', px: 1.5, py: 1, borderRadius: '16px 16px 4px 16px', bgcolor: '#a52a4b', color: 'common.white' }}>
    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{content}</Typography>
  </Box>
);

export const AssistantMessage = ({ content, products, onProductClick }) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const { text, images } = getAssistantContent(content);
  const selectedImage = images[selectedImageIndex];
  const showPreviousImage = () => setSelectedImageIndex((current) => (current - 1 + images.length) % images.length);
  const showNextImage = () => setSelectedImageIndex((current) => (current + 1) % images.length);

  return (
    <Box sx={{ alignSelf: 'flex-start', maxWidth: products?.length ? '100%' : '82%' }}>
      {text && <Box sx={{ display: 'inline-block', px: 1.5, py: 1, borderRadius: '16px 16px 16px 4px', bgcolor: '#fff', border: '1px solid #eadfdb' }}><Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{text}</Typography></Box>}
      {images.length > 0 && (
        <Box component="button" type="button" aria-label="Abrir imágenes de la respuesta" onClick={() => setImageViewerOpen(true)} sx={{ display: 'block', position: 'relative', width: '100%', maxWidth: 560, mt: 1.25, p: 0, border: 0, borderRadius: 2, overflow: 'hidden', bgcolor: '#f5eeea', cursor: 'zoom-in', textAlign: 'left' }}>
          <Box component="img" src={images[0]} alt="Imagen del producto" sx={{ display: 'block', width: '100%', height: { xs: 190, sm: 260 }, objectFit: 'cover' }} />
          {images.length > 1 && <Typography variant="caption" sx={{ position: 'absolute', right: 10, bottom: 10, px: 1, py: 0.5, borderRadius: 1, color: '#fff', bgcolor: 'rgba(0,0,0,0.58)' }}>1 / {images.length}</Typography>}
        </Box>
      )}
      {products?.length > 0 && <ProductGrid products={products} onProductClick={onProductClick} />}
      <Dialog open={imageViewerOpen} onClose={() => setImageViewerOpen(false)} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 2, bgcolor: '#171312' } }}>
        <IconButton aria-label="Cerrar imágenes" onClick={() => setImageViewerOpen(false)} sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1, color: '#fff', bgcolor: 'rgba(0,0,0,0.45)', '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' } }}><Close /></IconButton>
        <DialogContent sx={{ position: 'relative', display: 'grid', placeItems: 'center', p: { xs: 1, sm: 2 }, minHeight: { xs: 320, sm: 500 } }}>
          {selectedImage && <Box component="img" src={selectedImage} alt="Imagen del producto" sx={{ maxWidth: '100%', maxHeight: '78vh', objectFit: 'contain' }} />}
          {images.length > 1 && <>
            <IconButton aria-label="Imagen anterior" onClick={showPreviousImage} sx={{ position: 'absolute', left: 12, top: '50%', color: '#fff', bgcolor: 'rgba(0,0,0,0.45)', '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' } }}><NavigateBefore /></IconButton>
            <IconButton aria-label="Imagen siguiente" onClick={showNextImage} sx={{ position: 'absolute', right: 12, top: '50%', color: '#fff', bgcolor: 'rgba(0,0,0,0.45)', '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' } }}><NavigateNext /></IconButton>
            <Typography sx={{ position: 'absolute', bottom: 12, color: '#fff', bgcolor: 'rgba(0,0,0,0.55)', px: 1, py: 0.5, borderRadius: 1 }} variant="caption">{selectedImageIndex + 1} / {images.length}</Typography>
          </>}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export const TypingMessage = () => (
  <Box sx={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, borderRadius: '16px 16px 16px 4px', bgcolor: '#fff', border: '1px solid #eadfdb' }}>
    <CircularProgress size={14} sx={{ color: '#a52a4b' }} />
    <Typography variant="body2" color="text.secondary">Jazmin está buscando...</Typography>
  </Box>
);

const ChatMessage = ({ message, onProductClick }) => message.role === 'user'
  ? <UserMessage content={message.content} />
  : <AssistantMessage content={message.content} products={message.products} onProductClick={onProductClick} />;

export default ChatMessage;